import { ArrowDown, ArrowUp, Check, Sparkles } from "lucide-react";
import { optionState, type Level } from "@/lib/game.ts";
import { cn } from "@/lib/utils";

/** One level of Round 1 or 2: a ruled list of options, each a real button. */
export function ChoiceList({ level, disabled, onPick }: { level: Level; disabled: boolean; onPick: (i: number) => void }) {
  const solved = level.correct !== null;
  return (
    <ul role="list" className="animate-leaf-in overflow-hidden rounded-xl border border-border bg-card shadow-[0_1px_0_rgb(0_0_0/0.04),0_18px_36px_-24px_rgb(70_45_10/0.45)]">
      {level.choices.map((c, i) => {
        const st = optionState(level, i);
        const dir = level.wrong[i];
        const inert = solved || st === "wrong" || disabled;
        return (
          <li key={i} className="border-b border-border/80 last:border-b-0">
            <button
              type="button"
              disabled={inert && st !== "correct"}
              aria-disabled={inert}
              aria-pressed={st === "correct"}
              onClick={() => !inert && onPick(i)}
              className={cn(
                "group relative flex w-full items-start gap-4 px-4 py-4 text-left transition-colors sm:px-5",
                "before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-brand before:opacity-0 before:transition-opacity",
                st === "idle" && !solved && !disabled && "hover:bg-secondary/70 hover:before:opacity-100 focus-visible:bg-secondary/70 active:bg-secondary",
                st === "idle" && solved && "opacity-45",
                st === "hinted" && "bg-hint/15 before:bg-hint before:opacity-100",
                st === "wrong" && "bg-wrong/10 text-muted-foreground",
                st === "correct" && "animate-found bg-found text-found-foreground",
              )}
            >
              <span aria-hidden className={cn("mt-0.5 w-5 shrink-0 text-right font-display text-xl italic leading-none", st === "correct" ? "text-found-foreground/80" : "text-muted-foreground/70")}>
                {String.fromCharCode(97 + (i % 26))}
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn("block font-display text-[1.4rem] leading-snug", st === "wrong" && "line-through decoration-muted-foreground/50 decoration-1")}>{c.title}</span>
                {c.description && <span className={cn("mt-0.5 block font-ui text-[0.82rem] leading-snug", st === "correct" ? "text-found-foreground/85" : "text-muted-foreground")}>{c.description}</span>}
                {c.meta && (
                  <span className={cn("mt-1 block font-ui text-[0.7rem] font-medium uppercase tracking-[0.14em] tabular-nums", st === "correct" ? "text-found-foreground/90" : "text-muted-foreground/70")}>
                    {c.meta}
                  </span>
                )}
              </span>
              <span className="mt-1 flex shrink-0 items-center gap-1.5 font-ui text-xs font-medium">
                {st === "wrong" && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    {dir === "below" ? <ArrowDown className="size-3.5" aria-hidden /> : <ArrowUp className="size-3.5" aria-hidden />}
                    <span>Look {dir === "below" ? "below" : "above"}</span>
                  </span>
                )}
                {st === "hinted" && (
                  <span className="flex items-center gap-1 text-[color-mix(in_oklch,var(--hint)_45%,var(--foreground))]">
                    <Sparkles className="size-3.5" aria-hidden /> Candidate
                  </span>
                )}
                {st === "correct" && (
                  <span className="flex items-center gap-1.5">
                    <Check className="size-4" aria-hidden />
                    <span className="tabular-nums">+{level.earned}</span>
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
